import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { zohoCallbackBounceUrl } from '../../lib/zohoCallbackBounce.js';

/**
 * The public front door of Zoho Meeting's OAuth redirect (`/settings/integrations/zoho/callback`).
 *
 * Zoho redirects to the one registered URL on the central `app.<APP_DOMAIN>` origin, which has no
 * session — so this path cannot sit behind RequireAuth, or the visitor would be sent to /login and
 * the one-time `code` lost. Instead this gate runs first: when the `state` names a company other
 * than the host we are on, it replaces the location with the same path and query on that company's
 * subdomain (where the session lives); otherwise it renders its child route, which is the usual
 * guarded flow (RequireAuth, then ZohoOAuthCallbackPage).
 *
 * The decision is made once, from the URL the page loaded with.
 */
export function ZohoCallbackGate() {
  // Fixed at first render: a bounce must not be re-decided as the router re-renders.
  const [bounceUrl] = useState(() => zohoCallbackBounceUrl(window.location));

  useEffect(() => {
    if (bounceUrl) window.location.replace(bounceUrl);
  }, [bounceUrl]);

  if (!bounceUrl) return <Outlet />;

  return (
    <div className="flex flex-col items-center gap-4 py-16 text-center" aria-busy="true">
      <Loader2 className="size-10 animate-spin text-accent" />
      <p className="font-semibold text-fg">Returning you to your workspace…</p>
    </div>
  );
}
